// Opening hours for the public venue page, matching the app
// (PiattoRNMobileApp screens/business/BusinessScreenUtils + util/getOpenCloseHours):
// today's hours, open/closed, and "opens …" when closed.
//
// Differences from the app (deliberate):
//  - Uses the venue's IANA time zone (bizTimeZone) when present, so a visitor
//    in another time zone sees the venue's local open/closed state. The app
//    uses the phone's clock.
//  - Handles yesterday's hours that run past midnight (e.g. Fri 18:00–02:00
//    is still open at 01:00 Saturday).
//
// Hours fields: biz{Monday..Sunday}Open / Close as "HH:mm" strings.

export const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WEEK_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const parseHM = (v) => {
  if (v === null || v === undefined) return null;
  const m = String(v).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59) return null;
  return h * 60 + min;
};

const dayHours = (venue, dayName) => {
  const open = parseHM(venue?.[`biz${dayName}Open`]);
  const close = parseHM(venue?.[`biz${dayName}Close`]);
  if (open === null || close === null) return null;
  // Closing at or before opening means it closes after midnight.
  return { open, close: close <= open ? close + 24 * 60 : close };
};

/** "7:30am", "5pm" (app style: drops ":00"). */
export const formatMinutes = (mins) => {
  const m = ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
  const h24 = Math.floor(m / 60);
  const min = m % 60;
  const suffix = h24 < 12 ? "am" : "pm";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return min === 0 ? `${h12}${suffix}` : `${h12}:${String(min).padStart(2, "0")}${suffix}`;
};

/** Day of week and minutes since midnight at `now` in `timeZone` (or local). */
export function localDayAndMinutes(now, timeZone) {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).formatToParts(now);
      const get = (t) => parts.find((p) => p.type === t)?.value;
      const dayIndex = DAYS.indexOf(get("weekday"));
      if (dayIndex >= 0) {
        return { dayIndex, minutes: (Number(get("hour")) % 24) * 60 + Number(get("minute")) };
      }
    } catch {
      // Unknown time zone: fall back to the browser's clock.
    }
  }
  return { dayIndex: now.getDay(), minutes: now.getHours() * 60 + now.getMinutes() };
}

export const hasAnyHours = (venue) => DAYS.some((d) => dayHours(venue, d));

/** "5 November" from the app's dd/mm/yyyy bizEventDate (app shows "5th November"). */
export function formatEventDate(value) {
  const m = String(value || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  const suffix = [11, 12, 13].includes(day % 100) ? "th" : { 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th";
  return `${day}${suffix} ${months[month - 1]}`;
}

/**
 * Today's status.
 * Returns { known, isOpen, todayText, nextOpenText, eventDate }.
 *  - known=false when there are no hours for today (app: "Hours not available").
 */
export function getOpeningStatus(venue, now = new Date()) {
  const { dayIndex, minutes } = localDayAndMinutes(now, venue?.bizTimeZone);
  const today = DAYS[dayIndex];
  const yesterday = DAYS[(dayIndex + 6) % 7];
  const todayHours = dayHours(venue, today);
  const yHours = dayHours(venue, yesterday);

  const openFromYesterday = !!yHours && yHours.close > 24 * 60 && minutes < yHours.close - 24 * 60;
  const openToday = !!todayHours && minutes >= todayHours.open && minutes < todayHours.close;
  const isOpen = openFromYesterday || openToday;

  let nextOpenText = null;
  if (!isOpen) {
    for (let offset = 0; offset < 7; offset++) {
      const name = DAYS[(dayIndex + offset) % 7];
      const h = dayHours(venue, name);
      if (!h) continue;
      if (offset === 0 && h.open <= minutes) continue;
      // App says "opens 7am" for today and tomorrow; the web says "tomorrow" to be clear.
      const when = offset === 0 ? "" : offset === 1 ? "tomorrow " : `${name} `;
      nextOpenText = `opens ${when}${formatMinutes(h.open)}`;
      break;
    }
  }

  return {
    known: !!todayHours,
    isOpen,
    todayText: todayHours
      ? `${formatMinutes(todayHours.open)} – ${formatMinutes(todayHours.close)}`
      : "Hours not available",
    nextOpenText,
    eventDate: formatEventDate(venue?.bizEventDate),
  };
}

/** Week list Monday → Sunday: [{ day, text, isToday }]. Empty if no hours at all. */
export function getWeeklyHours(venue, now = new Date()) {
  if (!hasAnyHours(venue)) return [];
  const { dayIndex } = localDayAndMinutes(now, venue?.bizTimeZone);
  return WEEK_ORDER.map((day) => {
    const h = dayHours(venue, day);
    return {
      day,
      text: h ? `${formatMinutes(h.open)} – ${formatMinutes(h.close)}` : "Closed",
      isToday: DAYS[dayIndex] === day,
    };
  });
}
